import React from "react";

// Stylised "aligned-with" tiles for each supported disclosure / compliance
// framework. Colours are chosen to reflect each organisation's actual brand
// identity (verified via their public brand guidelines) while the geometry
// stays intentionally original, so the tiles read as "aligned with" rather
// than a pixel-perfect trademark reproduction.

const Tile = ({ children, bg, testId }) => (
  <div
    data-testid={testId}
    className="group relative flex h-[78px] w-full flex-col items-center justify-center overflow-hidden rounded-xl border border-[#e4e6dd] bg-white shadow-[0_1px_0_rgba(0,0,0,0.02)] transition-all hover:-translate-y-0.5 hover:shadow-md"
  >
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-[0.06] transition-opacity group-hover:opacity-[0.12]"
      style={{ background: bg }}
    />
    <div className="relative flex flex-col items-center justify-center">
      {children}
    </div>
  </div>
);

const Sub = ({ children }) => (
  <span className="mt-1 text-[9px] font-medium uppercase tracking-[0.14em] text-[#6b7268]">
    {children}
  </span>
);

export default function StandardsLogos({ className = "" }) {
  return (
    <div
      className={`grid grid-cols-3 gap-2.5 ${className}`}
      data-testid="standards-logos"
    >
      {/* GRI — navy #0E2E4F (2020 refresh) */}
      <Tile bg="#0E2E4F" testId="logo-gri">
        <div className="flex items-baseline gap-0.5">
          <span className="font-display text-[22px] font-bold leading-none" style={{ color: "#0E2E4F" }}>
            gri
          </span>
          <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#7BB661" }} />
        </div>
        <Sub>Global Reporting</Sub>
      </Tile>

      {/* TCFD — deep navy blue */}
      <Tile bg="#0f2b57" testId="logo-tcfd">
        <span className="font-display text-[20px] font-semibold tracking-tight leading-none" style={{ color: "#0f2b57" }}>
          TCFD
        </span>
        <Sub>Climate Disclosures</Sub>
      </Tile>

      {/* CDP — berry red #D11242 (official) */}
      <Tile bg="#D11242" testId="logo-cdp">
        <div className="flex items-center gap-1">
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <circle cx="9" cy="9" r="8" fill="#D11242" />
            <path d="M6 5.5 A4 4 0 1 0 6 12.5" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          </svg>
          <span className="font-display text-[20px] font-bold tracking-tight leading-none" style={{ color: "#D11242" }}>
            CDP
          </span>
        </div>
        <Sub>Carbon Disclosure</Sub>
      </Tile>

      {/* ESRS — EU blue #003399 with 12 gold stars ring */}
      <Tile bg="#003399" testId="logo-esrs">
        <div className="flex items-center gap-1.5">
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="10" cy="10" r="9.5" fill="#003399" />
            {Array.from({ length: 12 }).map((_, i) => {
              const a = (i * Math.PI) / 6 - Math.PI / 2;
              const cx = 10 + Math.cos(a) * 6.2;
              const cy = 10 + Math.sin(a) * 6.2;
              return <circle key={i} cx={cx} cy={cy} r="0.95" fill="#FFCC00" />;
            })}
          </svg>
          <span className="font-display text-[18px] font-semibold leading-none" style={{ color: "#003399" }}>
            ESRS
          </span>
        </div>
        <Sub>CSRD Directive</Sub>
      </Tile>

      {/* IFRS — bright blue #0094FF (foundation identity) */}
      <Tile bg="#0094FF" testId="logo-ifrs">
        <div className="flex items-center gap-1.5">
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <rect x="0" y="0" width="20" height="20" rx="4" fill="#0094FF" />
            <path d="M4.5 5 L15.5 5 L15.5 7.2 L11.5 7.2 L11.5 15.5 L8.5 15.5 L8.5 7.2 L4.5 7.2 Z" fill="#fff" />
          </svg>
          <span className="font-display text-[18px] font-semibold leading-none" style={{ color: "#0094FF" }}>
            IFRS
          </span>
        </div>
        <Sub>S1 · S2 (ISSB)</Sub>
      </Tile>

      {/* CBAM / SKDM — EU border-customs mark */}
      <Tile bg="#7A2E12" testId="logo-cbam">
        <div className="flex items-center gap-1.5">
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="10" cy="10" r="9.5" fill="#7A2E12" />
            <path d="M10 3 L10 17 M4 7 L16 7 M4 13 L16 13" stroke="#F4B841" strokeWidth="1.4" strokeLinecap="round" />
            <circle cx="10" cy="10" r="1.6" fill="#F4B841" />
          </svg>
          <span className="font-display text-[18px] font-semibold leading-none" style={{ color: "#7A2E12" }}>
            CBAM
          </span>
        </div>
        <Sub>SKDM · Border Carbon</Sub>
      </Tile>
    </div>
  );
}
